import { fDate } from 'src/utils/format-time';
import type { MetricBarType } from 'src/components/data-grid/generic-data-grid';

export const PAGE_PADDING_X = '24px';
export const CARD_HEIGHT = 80;
export const CHART_HEIGHT = 403;
export const CHART_INNER_HEIGHT = 300;
export const SECTION_GAP = 2;

// Fixed data-grid body height → internal vertical scroll (Competitors pattern).
export const GRID_HEIGHT = 600;

// Default visible metrics in the data grid, in the Figma maquette order (#10):
// Brand Searches, Blended CTR, Brand Traffic, Blended CPC, Ad Cost.
export const GRID_DEFAULT_METRICS = [
  'brand_searches',
  'blended_ctr',
  'total_brand_traffic',
  'blended_cpc',
  'ad_cost',
];

// Maquette column order (#10 "all columns - explore"). Drives the grid's left-to-right column
// order via the `columnOrder` prop; metrics not listed here fall back to the KPI catalog `order`
// (placed after the listed ones). The dimension column (search_term) is pinned separately and is
// not part of this list. `paid_ctr` isn't in the maquette → slotted with the CTR group.
export const GRID_COLUMN_ORDER = [
  'brand_searches',
  'blended_ctr',
  'paid_ctr',
  'organic_ctr',
  'search_paid_ctr',
  'shopping_paid_ctr',
  'pmax_ctr',
  'uncaptured_percentage',
  'total_brand_traffic',
  'organic_brand_traffic',
  'ad_brand_traffic',
  'shopping_brand_traffic',
  'pmax_brand_traffic',
  'uncaptured_brand_traffic',
  'blended_cpc',
  'cpc',
  'search_cpc',
  'shopping_cpc',
  'pmax_cpc',
  'ad_cost',
  'search_ad_cost',
  'shopping_ad_cost',
  'pmax_ad_cost',
];

// In-cell bar per metric (maquette #10): magnitude bar (value ÷ page-max), composition stacked
// bar for Brand Traffic, no bar for the Blended CPC ratio.
export const GRID_BAR_TYPES: Record<string, MetricBarType> = {
  brand_searches: 'magnitude',
  total_brand_traffic: 'composition',
  blended_ctr: 'magnitude',
  paid_ctr: 'magnitude',
  organic_ctr: 'magnitude',
  search_paid_ctr: 'magnitude',
  shopping_paid_ctr: 'magnitude',
  pmax_ctr: 'magnitude',
  uncaptured_percentage: 'magnitude',
  ad_cost: 'magnitude',
  search_ad_cost: 'magnitude',
  shopping_ad_cost: 'magnitude',
  pmax_ad_cost: 'magnitude',
  blended_cpc: 'none',
};

// Per-column minWidth from the Figma maquette (#10 "all columns - explore"). Each value is the
// maquette column-group width (metric value + the inline evolution sub-column). Used as a minWidth
// floor with flex:1 kept, so columns still stretch to fill the viewport.
export const GRID_COLUMN_MIN_WIDTHS: Record<string, number> = {
  search_term: 203,
  brand_searches: 242,
  blended_ctr: 229,
  paid_ctr: 212,
  organic_ctr: 212,
  search_paid_ctr: 212,
  shopping_paid_ctr: 212,
  pmax_ctr: 212,
  uncaptured_percentage: 212,
  total_brand_traffic: 262,
  organic_brand_traffic: 160,
  ad_brand_traffic: 160,
  shopping_brand_traffic: 160,
  pmax_brand_traffic: 160,
  uncaptured_brand_traffic: 160,
  blended_cpc: 160,
  cpc: 160,
  search_cpc: 160,
  shopping_cpc: 160,
  pmax_cpc: 160,
  ad_cost: 240,
  search_ad_cost: 209,
  shopping_ad_cost: 209,
  pmax_ad_cost: 209,
};

// Width of the inline evolution sub-column, already baked into GRID_COLUMN_MIN_WIDTHS above.
const EVOLUTION_SUBCOL_WIDTH = 66;

// Readable floors (mirror the grid's generic defaults) so the no-compare reduction never makes a
// column too narrow — especially no-bar metrics (e.g. Blended CPC) that only render a value.
const MIN_BAR_WIDTH = 190;
const MIN_NO_BAR_WIDTH = 150;

// Column minWidths for the current compare state. When no date comparison is active the inline
// evolution indicator is not rendered, so metric columns drop the evolution sub-column width and
// get a bit narrower — but never below the readable floor above, and never wider than their
// compare-on width. The dimension column has no evolution → left untouched.
export const exploreColumnMinWidths = (compareActive: boolean): Record<string, number> =>
  compareActive
    ? GRID_COLUMN_MIN_WIDTHS
    : Object.fromEntries(
        Object.entries(GRID_COLUMN_MIN_WIDTHS).map(([field, width]) => {
          if (field === 'search_term') return [field, width];
          const isNoBar = (GRID_BAR_TYPES[field] ?? 'none') === 'none';
          const floor = Math.min(isNoBar ? MIN_NO_BAR_WIDTH : MIN_BAR_WIDTH, width);
          return [field, Math.max(width - EVOLUTION_SUBCOL_WIDTH, floor)];
        })
      );

// Tokens couleurs Figma (Rebranding — Signal 2026 Q2)
export const EXPLORE_COLORS = {
  borderNeutral: '#EAEAEA',
  gridBorder: 'rgba(145, 158, 171, 0.2)', // séparateurs de cellules pleins — Figma color/azure/62 @ 20%
  rowHover: 'rgba(145, 158, 171, 0.08)', // fond de ligne au survol (azure 62 @ 8%)
  headerBg: '#FBFBFC', // Figma color/azure/90 @ 14% aplati sur blanc (opaque → pas de double-teinte sur les colonnes épinglées)
  footerBg: '#FAFAFA', // fond du footer de pagination de la datagrid
  labelMuted: '#637381',
  valueDark: '#333C47',
  textDisabled: '#919EAB',
  cardSelectedBg: '#F9FAFC',
  tabActive: '#7655C1',
};

// Couleurs de séries du graphe barres+ligne (réutilise la palette Funnel)
export const CHART_COLORS = {
  bar: '#ffb700', // métrique en barres (ex. Ad Cost)
  line: '#5119b7', // métrique en ligne (ex. Brand Traffic)
};

export interface ExploreTab {
  label: string;
  value: number;
  groupby: string[];
}

/**
 * Onglets de base de la datagrid Explore. L'onglet "Countries" (value 1,
 * groupby region_name) n'est inclus que si la sélection courante porte sur
 * PLUS D'UN pays : un filtre sur un seul pays (ou "All" sur un workspace
 * mono-pays) ne donnerait qu'une ligne, donc le découpage par pays n'a pas de
 * sens (et le bandeau pays est lui aussi masqué dans ce cas).
 * Les autres `value` restent stables (0, 2, 3, + 8 plateformes) pour ne pas
 * casser les lookups groupby/tri.
 */
export const buildExploreBaseTabs = (
  timestep: string | undefined,
  selectedCountries: string[] | undefined
): ExploreTab[] => {
  const showCountriesTab = (selectedCountries?.length ?? 0) > 1;
  const dateField = (timestep ?? 'date').toLowerCase().replace('date_day', 'date');
  return [
    { label: 'dashboard.global.search_terms', value: 0, groupby: ['search_term'] },
    ...(showCountriesTab
      ? [{ label: 'dashboard.global.countries', value: 1, groupby: ['region_name'] }]
      : []),
    { label: 'dashboard.global.devices', value: 2, groupby: ['device_type'] },
    { label: 'dashboard.global.date', value: 3, groupby: [dateField] },
  ];
};

export const formatExploreLabels = (
  timeSeries: any[],
  timestep: string,
  _lang?: string
): string[] =>
  (timeSeries ?? []).map((item) => {
    const dateStr =
      timestep === 'date_day'
        ? item?.date
        : item?.date ?? item?.year_week ?? item?.date_interval ?? '';
    return timestep === 'date_day' ? fDate(dateStr, 'yyyy-MM-dd') : dateStr;
  });
